import { useEffect, useState } from 'react'
import { Caption, COLORS, Badge } from './ui'

const G = 7
const N = G * G
const CENTER = Math.floor(N / 2)
const ATTN = [0, 6, 21, 27, 42, 48, 10, 38]

// Image → patches → tokens (+ positional embedding) → self-attention.
export function ViT2D() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const CELL = mobile ? 32 : 34
  const GAP = mobile ? 2 : 4
  const gridPx = G * CELL + (G - 1) * GAP

  const tokenPos = i => {
    const r = Math.floor(i / G), c = i % G
    return { x: c * (CELL + GAP) + CELL / 2, y: r * (CELL + GAP) + CELL / 2 }
  }

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: mobile ? 8 : 16, padding: mobile ? '80px 14px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <Badge color={COLORS.indigoLight}>Vision Transformer (ViT)</Badge>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 10 : 22, flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
        {/* Image split into patches */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', width: gridPx, height: gridPx, borderRadius: 8, overflow: 'hidden' }}>
            <img src="/cv/ref-cat.png" alt="cat" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${G},1fr)`, gap: 1, background: 'rgba(99,102,241,0.4)' }}>
              {Array.from({ length: N }).map((_, i) => (
                <div key={i} style={{ outline: i === CENTER ? `2px solid ${COLORS.purple}` : 'none', outlineOffset: -2 }} />
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textFaint }}>image → {N} patches</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: COLORS.textDim, fontSize: 11 }}>
          <span style={{ color: COLORS.indigo, fontSize: 22, opacity: 0.6, transform: mobile ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>→</span>
          <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>flatten</span>
          <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.green }}>+ position</span>
        </div>

        {/* Token sequence + attention */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', width: gridPx, height: gridPx }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${G}, ${CELL}px)`, gap: GAP }}>
              {Array.from({ length: N }).map((_, i) => {
                const r = Math.floor(i / G), c = i % G
                return (
                  <div key={i} style={{
                    width: CELL, height: CELL, borderRadius: 5,
                    backgroundImage: 'url(/cv/ref-cat.png)',
                    backgroundSize: `${G * 100}% ${G * 100}%`,
                    backgroundPosition: `${(c / (G - 1)) * 100}% ${(r / (G - 1)) * 100}%`,
                    outline: i === CENTER ? `2px solid ${COLORS.purple}` : `1px solid ${COLORS.indigo}55`,
                    outlineOffset: -1,
                  }} />
                )
              })}
            </div>
            {/* attention lines from the centre token */}
            <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width={gridPx} height={gridPx}>
              {ATTN.map((t, i) => {
                const a = tokenPos(CENTER), b = tokenPos(t)
                return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={COLORS.purple} strokeWidth="1.4" opacity="0.55" />
              })}
              <circle cx={tokenPos(CENTER).x} cy={tokenPos(CENTER).y} r="4" fill={COLORS.purple} />
            </svg>
          </div>
          <div style={{ fontSize: 12.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textFaint }}>tokens → self-attention</div>
        </div>
      </div>

      <Caption>
        A ViT chops the image into patches, flattens each into a token, and adds a <strong>positional
        embedding</strong> so order isn’t lost. Then <em>self-attention</em> lets every token look at every other —
        the highlighted patch attends to related regions across the whole image, not just its neighbours.
      </Caption>
    </div>
  )
}
